import './pique-ai-panel-components';

import {
  getPageRootByElement,
  stopPropagation,
} from '@blocksuite/affine-shared/utils';
import type { BaseSelection } from '@blocksuite/block-std';
import { WidgetComponent } from '@blocksuite/block-std';
import type {
  AffineAIPanelState,
  AffineAIPanelWidgetConfig,
  AIError,
  EdgelessRootService,
} from '@blocksuite/blocks';

// Extend the config interface to include our auto-insert handler
interface PiqueAIPanelWidgetConfig extends AffineAIPanelWidgetConfig {
  autoInsertHandler?: () => void;
}
import { AFFINE_FORMAT_BAR_WIDGET, PageRootService } from '@blocksuite/blocks';
import { assertExists } from '@blocksuite/global/utils';
import {
  autoUpdate,
  computePosition,
  type ComputePositionConfig,
  flip,
  offset,
  shift,
} from '@floating-ui/dom';
import { css, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';

import {
  PIQUE_VIEWPORT_OVERLAY_WIDGET,
  type PiqueViewportOverlayWidget,
} from './pique-viewport-overlay-widget';

export const PIQUE_AI_PANEL_WIDGET = 'pique-ai-panel-widget';

@customElement(PIQUE_AI_PANEL_WIDGET)
export class PiqueAIPanelWidget extends WidgetComponent {
  private _abortController = new AbortController();

  private _answer: string | null = null;

  private readonly _inputFinish = (text: string) => {
    this._inputText = text;
    this.generate();
  };

  private _inputText: string | null = null;

  private readonly _onKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    const { state } = this;
    if (state !== 'generating' && state !== 'input') {
      return;
    }

    if (event.key === 'Escape') {
      if (state === 'generating') {
        this.stopGenerating();
      } else {
        this.hide();
      }
    }
  };

  private readonly _resetAbortController = () => {
    if (this.state === 'generating') {
      this._abortController.abort();
    }
    this._abortController = new AbortController();
  };

  private _selection?: BaseSelection[];

  private _stopAutoUpdate?: undefined | (() => void);

  static override styles = css`
    :host {
      display: flex;
      outline: none;
      border-radius: var(--8, 8px);
      border: 1px solid var(--affine-border-color);
      background: var(--affine-background-overlay-panel-color);
      box-shadow: var(--affine-overlay-shadow);

      position: absolute;
      width: 630px; /* Default page width */
      height: auto;
      top: 0;
      left: 0;
      overflow-y: auto;
      scrollbar-width: none !important;
      z-index: var(--affine-z-index-popover);

      /* Pique-specific customizations */
      --pique-ai-panel-primary: #6366f1;
      --pique-ai-panel-accent: #8b5cf6;
      --pique-ai-panel-bg: var(--affine-background-overlay-panel-color);
      --pique-ai-panel-border: rgba(99, 102, 241, 0.2);

      /* Removed backdrop-filter to match original - no page blur */
      background: var(--pique-ai-panel-bg);
      border-color: var(--pique-ai-panel-border);
    }

    /* Edgeless mode styling */
    :host(.edgeless) {
      width: 430px;
    }

    /* Generating state styling */
    :host(.generating) {
      background: transparent;
      border: none;
      box-shadow: none;
    }

    .ai-panel-container {
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      width: 100%;
      height: fit-content;
      padding: 8px 0;

      /* Pretty blended styling */
      background: linear-gradient(
        135deg,
        rgba(99, 102, 241, 0.05) 0%,
        rgba(139, 92, 246, 0.05) 100%
      );
      border-radius: 18px;
      border: 1px solid rgba(99, 102, 241, 0.08);
      backdrop-filter: blur(8px);
    }

    .ai-panel-container:not(:has(ai-panel-generating)) {
      gap: 8px;
    }

    .ai-panel-container:has(ai-panel-answer),
    .ai-panel-container:has(ai-panel-error),
    .ai-panel-container:has(ai-panel-generating:has(generating-placeholder)) {
      padding: 12px 0;
    }

    :host([data-state='hidden']) {
      display: none;
    }

    /* Enhanced generating state */
    :host([data-state='generating']) .ai-panel-container {
      background: linear-gradient(
        135deg,
        rgba(99, 102, 241, 0.08) 0%,
        rgba(139, 92, 246, 0.08) 100%
      );
      border: 1px solid rgba(99, 102, 241, 0.12);
      box-shadow: 0 4px 20px rgba(99, 102, 241, 0.1);
    }

    /* Custom AI Panel Component Styles */
    .ai-input-container {
      padding: 12px;
    }

    .ai-input-container input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid var(--pique-ai-panel-border);
      border-radius: 6px;
      font-size: 14px;
      outline: none;
      background: var(--affine-background-primary-color);
      color: var(--affine-text-primary-color);
    }

    .ai-input-container input:focus {
      border-color: var(--pique-ai-panel-primary);
      box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
    }

    .ai-answer-container {
      padding: 12px;
      max-height: 320px;
      overflow-y: auto;
    }

    .ai-generating-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px;
      gap: 8px;
    }

    .loading-indicator {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--pique-ai-panel-primary);
      font-size: 14px;
    }

    .ai-actions-container {
      display: flex;
      gap: 8px;
      padding: 12px;
      border-top: 1px solid var(--affine-border-color);
    }

    .action-button,
    .stop-button {
      padding: 6px 12px;
      border: 1px solid var(--pique-ai-panel-border);
      border-radius: 4px;
      background: var(--affine-background-primary-color);
      color: var(--affine-text-primary-color);
      cursor: pointer;
      font-size: 12px;
      transition: all 0.2s ease;
    }

    .action-button:hover,
    .stop-button:hover {
      background: var(--pique-ai-panel-primary);
      color: white;
      border-color: var(--pique-ai-panel-primary);
    }

    .ai-error-container {
      padding: 12px;
    }

    .error-message {
      color: var(--affine-error-color);
      font-size: 14px;
      margin-bottom: 8px;
    }

    .generating-content {
      padding: 0 12px;
      color: var(--affine-text-primary-color);
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    /* Edgeless mode specific adjustments */
    :host(.edgeless.generating) .generating-content {
      padding: 16px;
      min-height: 50px;
      justify-content: center;
      align-items: center;
    }
  `;

  ctx: unknown = null;

  /**
   * You can evaluate this method multiple times to regenerate the answer.
   */
  generate = () => {
    assertExists(this.config);
    const text = this._inputText;
    assertExists(text);
    assertExists(this.config.generateAnswer);

    this._resetAbortController();

    // reset answer
    this._answer = null;

    const update = (answer: string) => {
      this._answer = answer;
      this.requestUpdate();
    };
    const finish = (type: 'success' | 'error' | 'aborted', err?: AIError) => {
      if (type === 'aborted') return;

      assertExists(this.config);
      if (type === 'error') {
        this.state = 'error';
        this.config.errorStateConfig.error = err;
      } else {
        this.state = 'finished';
        this.config.errorStateConfig.error = undefined;

        const configWithAutoInsert = this.config as {
          autoInsertHandler?: () => void;
        } | null;
        if (this._answer && configWithAutoInsert?.autoInsertHandler) {
          setTimeout(() => {
            configWithAutoInsert.autoInsertHandler?.();
            this.hide();
          }, 100);
        }
      }

      this._resetAbortController();
    };

    this.scrollTop = 0; // reset scroll top
    this.state = 'generating';
    this.config.generateAnswer({
      input: text,
      update,
      finish,
      signal: this._abortController.signal,
    });
  };

  hide = () => {
    this._resetAbortController();
    this.state = 'hidden';
    this._stopAutoUpdate?.();
    this._inputText = null;
    this._answer = null;
    this._stopAutoUpdate = undefined;
    this.viewportOverlayWidget?.unlock();
  };

  onInput = (text: string) => {
    this._inputText = text;
  };

  stopGenerating = () => {
    this._abortController.abort();
    this.state = 'finished';
    if (!this.answer) {
      this.hide();
    }
  };

  toggle = (reference: Element, input?: string) => {
    if (input) {
      this._inputText = input;
      this.generate();
    } else {
      // reset state
      this.hide();
      this.state = 'input';
    }

    this._autoUpdatePosition(reference);
  };

  private _autoUpdatePosition(reference: Element) {
    // workaround for the case that the reference contains children block elements, like:
    // paragraph
    //    child paragraph
    {
      const childrenContainer = reference.querySelector(
        '.affine-block-children-container'
      );
      if (childrenContainer && childrenContainer.previousElementSibling) {
        reference = childrenContainer.previousElementSibling;
      }
    }

    this._stopAutoUpdate?.();
    this._stopAutoUpdate = autoUpdate(reference, this, () => {
      if (this.state === 'generating') {
        const rootService = this.host.std.spec.getService('affine:page');
        // In edgeless, we need to calculate the center of the viewport
        if (!(rootService instanceof PageRootService)) {
          const viewport = (rootService as unknown as EdgelessRootService)
            .viewport;
          const centerX = viewport.left + viewport.width / 2;
          const centerY = viewport.top + viewport.height / 2;

          this.style.position = 'fixed';
          this.style.left = `${centerX}px`;
          this.style.top = `${centerY}px`;
          this.style.transform = 'translate(-50%, -50%)';
        } else {
          // In page mode, we can just center it in the window
          this.style.position = 'fixed';
          this.style.top = '50%';
          this.style.left = '50%';
          this.style.transform = 'translate(-50%, -50%)';
        }
        return;
      }

      // For other states, revert to absolute positioning relative to the reference element
      this.style.position = 'absolute';
      this.style.transform = '';

      computePosition(reference, this, this._calcPositionOptions(reference))
        .then(({ x, y }) => {
          this.style.left = `${x}px`;
          this.style.top = `${y}px`;
        })
        .catch(console.error);
    });
  }

  private _calcPositionOptions(
    reference: Element
  ): Partial<ComputePositionConfig> {
    const overflowOptions = {
      padding: 20,
    };

    // edgeless element
    if (
      !getPageRootByElement(reference) &&
      !reference.closest('edgeless-block-portal-note')
    ) {
      return {
        placement: 'right-start',
        middleware: [
          offset({ mainAxis: 16 }),
          flip({
            mainAxis: true,
            crossAxis: true,
            flipAlignment: true,
            ...overflowOptions,
          }),
          shift({
            crossAxis: true,
            ...overflowOptions,
          }),
        ],
      };
    }
    // block element in page editor or note in edgeless
    else {
      return {
        placement: 'bottom-start',
        middleware: [offset(8), flip(overflowOptions), shift(overflowOptions)],
      };
    }
  }

  private _restoreSelection() {
    if (this._selection) {
      this.host.selection.set([...this._selection]);
      if (this.state === 'hidden') {
        this._selection = undefined;
      }
    }
  }

  private _isEdgelessContext(): boolean {
    try {
      const rootService = this.host.std.spec.getService('affine:page');
      return !(rootService instanceof PageRootService);
    } catch {
      return false;
    }
  }

  override connectedCallback() {
    super.connectedCallback();

    this.tabIndex = -1;
    this.disposables.addFromEvent(this, 'wheel', stopPropagation, {
      passive: true,
    });
    this.disposables.addFromEvent(this, 'pointerdown', stopPropagation);
    this.disposables.addFromEvent(this, 'pointerup', stopPropagation);
    this.disposables.addFromEvent(this, 'keydown', this._onKeyDown);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._stopAutoUpdate?.();
  }

  override render() {
    if (this.state === 'hidden') {
      return nothing;
    }

    if (!this.config) return nothing;
    const config = this.config;

    this.updateComplete
      .then(() => {
        this.focus();
      })
      .catch(console.error);

    const mainTemplate = choose(this.state, [
      [
        'input',
        () =>
          html`<pique-ai-panel-input
            .onFinish=${this._inputFinish}
            .onInput=${this.onInput}
          ></pique-ai-panel-input>`,
      ],
      [
        'generating',
        () => html`
          <div class="generating-content">
            ${this._answer
              ? config.answerRenderer(this._answer, this.state)
              : nothing}
            <pique-ai-panel-generating
              .config=${config.generatingStateConfig}
              .stopGenerating=${this.stopGenerating}
              .withAnswer=${!!this._answer}
            ></pique-ai-panel-generating>
          </div>
        `,
      ],
      [
        'finished',
        () => html`
          <pique-ai-panel-answer
            .config=${config.finishStateConfig}
            .copy=${config.copy}
            .host=${this.host}
          >
            ${this.answer && config.answerRenderer(this.answer, this.state)}
          </pique-ai-panel-answer>
        `,
      ],
      [
        'error',
        () => html`
          <pique-ai-panel-error
            .config=${config.errorStateConfig}
            .copy=${config.copy}
            .withAnswer=${!!this.answer}
            .host=${this.host}
          >
            ${this.answer && config.answerRenderer(this.answer, this.state)}
          </pique-ai-panel-error>
        `,
      ],
    ]);

    return html`<div class="ai-panel-container">${mainTemplate}</div>`;
  }

  protected override willUpdate(changed: PropertyValues): void {
    const prevState = changed.get('state');

    // Set CSS classes based on context and state
    const isEdgeless = this._isEdgelessContext();
    this.classList.toggle('edgeless', isEdgeless);
    this.classList.toggle('generating', this.state === 'generating');

    if (prevState) {
      if (prevState === 'hidden') {
        this._selection = this.host.selection.value;
        requestAnimationFrame(() => {
          this.scrollIntoView({ block: 'center' });
        });
      } else {
        this.host.updateComplete
          .then(() => {
            if (this.state !== 'hidden') {
              this.focus();
            }
          })
          .catch(console.error);
        this._restoreSelection();
      }

      // Auto-insert is now handled in the generate() finish callback

      // Update format bar
      const rootBlockId = this.host.doc.root?.id;
      const formatBar = rootBlockId
        ? this.host.view.getWidget(AFFINE_FORMAT_BAR_WIDGET, rootBlockId)
        : null;
      if (formatBar) formatBar.requestUpdate();
    }

    if (this.state !== 'hidden') {
      this.viewportOverlayWidget?.lock();
    } else {
      this.viewportOverlayWidget?.unlock();
    }

    this.dataset['state'] = this.state;
  }

  get answer() {
    return this._answer;
  }

  get inputText() {
    return this._inputText;
  }

  get viewportOverlayWidget() {
    const rootId = this.host.doc.root?.id;
    return rootId
      ? (this.host.view.getWidget(
          PIQUE_VIEWPORT_OVERLAY_WIDGET,
          rootId
        ) as PiqueViewportOverlayWidget)
      : null;
  }

  @property({ attribute: false })
  accessor config: PiqueAIPanelWidgetConfig | null = null;

  @query('pique-ai-panel-generating')
  accessor generatingElement:
    | (HTMLElement & { updateLoadingProgress?: (progress?: number) => void })
    | null = null;

  @property()
  accessor state: AffineAIPanelState = 'hidden';
}

declare global {
  interface HTMLElementTagNameMap {
    [PIQUE_AI_PANEL_WIDGET]: PiqueAIPanelWidget;
  }
}
